import type { DealProduct, DealProductAddOn, DarwinBaselineProduct, DarwinBaselineProducts } from '@makemydeal/dr-shared-types';
import type {
    VehicleProtectionState,
    DealProductSummary,
    DealerProduct,
    DraftDealProduct,
    RatedProductRates,
    VppSurcharge,
    ProductWithProductCode,
    ProductAddOnDetailsNonOptional,
    VppTermField,
    OverriddenTermFields
} from '@makemydeal/dr-dash-types';
import { TERM_FIELD_TO_RATE_ATTRIBUTE } from '@makemydeal/dr-dash-types';
import type { OfferType, Rate, DynamicSurcharge, RatedProtectionProduct } from '@makemydeal/dr-platform-types';
import {
    OfferTypeEnum,
    VppDealType,
    AccelerateVehicleCondition,
    VppInventoryType,
    ExtendedOfferTypeEnum
} from '@makemydeal/dr-platform-types';
import _ from 'lodash';

type CalculateSummaryInput = {
    products: DraftDealProduct[];
    term?: number;
};

export function calculateVehicleProtectionSummary({ products, term }: CalculateSummaryInput): DealProductSummary {
    const initialSummary = {
        totalProductPrice: 0,
        totalProductMarkup: 0,
        totalProductCost: 0,
        productMonthlyPayment: 0
    };

    const selectedProducts = products.filter((product) => product.selected);

    const summary = selectedProducts.reduce((summary, product) => {
        const productPrice = product.productPrice || 0;
        const productMarkup = product.productMarkup?.markupDollarAmount || 0;
        const productCost = product.productCost || 0;

        summary.totalProductPrice += productPrice;
        summary.totalProductMarkup += productMarkup;
        summary.totalProductCost += productCost;

        return summary;
    }, initialSummary);

    if (term) {
        summary.productMonthlyPayment = summary.totalProductPrice / term;
    }

    return summary;
}

const mapToProductCodes = (products: ProductWithProductCode[]): string[] => {
    return products.map((product) => product.productCode);
};

export const getModifiedProducts = ({ modifiedProducts }: VehicleProtectionState, newModifiedProducts: DraftDealProduct[]) => {
    const productCodesToAdd = mapToProductCodes(newModifiedProducts.filter(isProductWithCode));
    const uniqueProductCodes = _.uniq([...modifiedProducts, ...productCodesToAdd]);

    return uniqueProductCodes;
};

export const getRequestedSurcharges = (surcharges: VppSurcharge[] = []) => {
    const usedSurchargesAttributesSet = new Set<string>();
    const requiredSurchargesAttributesSet = new Set<string>();

    surcharges.forEach(({ resolvingFields, supplementalForProducts, optionalForProducts }) => {
        if (resolvingFields?.length) {
            const isUsed = optionalForProducts?.length || supplementalForProducts?.length;
            const isRequired = supplementalForProducts?.length;

            if (isRequired) {
                resolvingFields.forEach((resolvingField) => requiredSurchargesAttributesSet.add(resolvingField));
            }

            if (isUsed) {
                resolvingFields.forEach((resolvingField) => usedSurchargesAttributesSet.add(resolvingField));
            }
        }
    });

    return {
        usedSurchargesAttributes: Array.from(usedSurchargesAttributesSet),
        requiredSurchargesAttributes: Array.from(requiredSurchargesAttributesSet)
    };
};

const vehicleConditionToFieInventoryTypeMappers: { [key in AccelerateVehicleCondition]: VppInventoryType } = {
    [AccelerateVehicleCondition.NEW]: VppInventoryType.NEW,
    [AccelerateVehicleCondition.USED]: VppInventoryType.USED,
    [AccelerateVehicleCondition.CERTIFIED]: VppInventoryType.CPO,
    // VPP has no dedicated "other" inventory type; treat it like USED for rating/eligibility purposes
    [AccelerateVehicleCondition.OTHER]: VppInventoryType.USED
};

const offerTypeToFieDealTypeMapper: { [key in ExtendedOfferTypeEnum]: VppDealType } = {
    [OfferTypeEnum.CASH]: VppDealType.CASH,
    [OfferTypeEnum.FINANCE]: VppDealType.FINANCE,
    [OfferTypeEnum.LEASE]: VppDealType.LEASE,
    [ExtendedOfferTypeEnum.BALLOON]: VppDealType.BALLOON
};

const conditionToLabelMappers: { [key in AccelerateVehicleCondition]: string } = {
    [AccelerateVehicleCondition.NEW]: 'New',
    [AccelerateVehicleCondition.USED]: 'Used',
    [AccelerateVehicleCondition.CERTIFIED]: 'CPO',
    [AccelerateVehicleCondition.OTHER]: 'Other'
};

export const mapAmdToVppVehicleCondition = (vehicleCondition: AccelerateVehicleCondition): VppInventoryType => {
    return vehicleConditionToFieInventoryTypeMappers[vehicleCondition?.toLowerCase() as AccelerateVehicleCondition];
};

export const mapOfferTypeToVppDealType = (offerType: OfferType): VppDealType => {
    return offerTypeToFieDealTypeMapper[offerType?.toLowerCase() as OfferType];
};

export const mapAmdToVppVehicleConditionLabel = (vehicleCondition: AccelerateVehicleCondition): string => {
    return conditionToLabelMappers[vehicleCondition];
};

const toDraftProduct = (dealerProduct: DealerProduct): DraftDealProduct => {
    const { dealerSettings } = dealerProduct;

    return {
        productName: dealerProduct.productName,
        productCategoryCode: dealerProduct.productCategoryCode,
        productCode: dealerProduct.productCode,
        selected: false,
        productProviderName: dealerSettings?.providerName,
        productProviderAddress: dealerSettings?.providerAddress
    };
};

const applyRates = (product: DraftDealProduct, rates: RatedProductRates): DraftDealProduct => {
    const productAddOnDetails = mapDynamicSurchargesToProductAddOnDetails(rates.dynamicSurcharges);

    const ratedProduct: DraftDealProduct = {
        ...product,
        productPrice: rates.productPrice,
        productCost: rates.productCost,
        productInitialCost: rates.productCost,
        productDeductible: rates.productDeductible,
        productDeductibleMethod: rates.deductibleApplyType,
        productCoverageLength: rates.productCoverageLength,
        productCoverageMiles: rates.productCoverageMiles,
        productProviderPlan: rates.productProviderPlan,
        minPriceAllowedByLender: rates.minPrice,
        maxPriceAllowedByLender: rates.maxPrice,
        productAddOnDetails
    };
    const markupDollarAmount = rates.productPrice - rates.productCost;

    if (typeof markupDollarAmount === 'number' && !Number.isNaN(markupDollarAmount)) {
        ratedProduct.productMarkup = {
            markupMethod: 'Dollar',
            markupDollarAmount: markupDollarAmount
        };
    }

    if (rates.productServiceInterval) {
        ratedProduct.productServiceInterval = rates.productServiceInterval;
    }

    return ratedProduct;
};

export const syncVppProducts = (
    { products: existingProducts, modifiedProducts }: VehicleProtectionState,
    newDealerProducts: DealerProduct[]
): DraftDealProduct[] => {
    const existingManualProducts = existingProducts.filter((product) => product.isManualProduct);
    const existingModifiedProducts = existingProducts.filter(
        ({ productCode, isManualProduct }) => !isManualProduct && productCode && modifiedProducts.includes(productCode)
    );
    const mergedProducts: DraftDealProduct[] = [...existingModifiedProducts, ...existingManualProducts];

    newDealerProducts.forEach((newDealerProduct) => {
        const isProductModified = modifiedProducts.includes(newDealerProduct.productCode);

        if (!isProductModified) {
            mergedProducts.push(toDraftProduct(newDealerProduct));
        }

        // NOTE: if product has provider address, we need to update it in the existing product
        const providerAddress = newDealerProduct.dealerSettings?.providerAddress;
        if (providerAddress) {
            const existingProductToUpdate = existingProducts.find(
                (product) => product.productCode === newDealerProduct.productCode
            );

            // NOTE: only if existing product doesn't have provider address
            if (existingProductToUpdate && !existingProductToUpdate.productProviderAddress) {
                existingProductToUpdate.productProviderAddress = providerAddress;
            }
        }
    });

    return mergedProducts;
};

export const updateProductsWithRates = ({
    products,
    rates,
    modifiedProducts,
    targetProducts
}: VehicleProtectionState): DraftDealProduct[] => {
    const productsToUpdate: DraftDealProduct[] = _.cloneDeep(products);

    for (const product of productsToUpdate) {
        const isProductModified = product.productCode && modifiedProducts.includes(product.productCode);

        if (!isProductModified) {
            const productRates = rates.find((rate) => rate.productCode === product.productCode);

            if (productRates && productRates.rates.length > 0) {
                const recommendedRate = productRates.rates.find((rate) => rate.recommended);

                if (recommendedRate) {
                    Object.assign(product, applyRates(product, recommendedRate));
                }
            }
        }
    }

    for (const targetProduct of targetProducts) {
        const existingProduct = productsToUpdate.find(
            (product) =>
                product.productCode === targetProduct.productCode &&
                product.productCategoryCode === targetProduct.productCategoryCode
        );

        if (existingProduct && targetProduct.profitLocation && existingProduct.profitLocation !== targetProduct.profitLocation) {
            existingProduct.profitLocation = targetProduct.profitLocation;
        }
    }

    return productsToUpdate;
};

export const isDealProduct = (product: DraftDealProduct): product is DealProduct =>
    product.productCategoryCode !== undefined && product.productPrice !== undefined;

export const isProductWithCode = (product: DraftDealProduct): product is ProductWithProductCode =>
    product.productCode !== undefined;

export const mapToInitialDealXgProducts = (products: DraftDealProduct[]): DraftDealProduct[] => {
    return products.map((product) => ({
        productCode: product.productCode,
        productCost: product.productCost
    }));
};

export const mapToPaymentProductCodes = (products: DraftDealProduct[]): string[] => {
    return products.filter((product) => product.selected).map((product) => product.productCode!);
};

export const mapToDealerProducts = (products: DealerProduct[]): Partial<DealerProduct>[] => {
    return products.map((product) => ({
        productCode: product.productCode,
        rateRequirements: product.rateRequirements,
        dealerSettings: product.dealerSettings
    }));
};

export const selectAllEligibleProducts = ({ products, dealerProducts, rates }: VehicleProtectionState): DraftDealProduct[] => {
    products.forEach((product) => {
        const dealerProduct = dealerProducts.find((dealerProduct) => dealerProduct.productCode === product.productCode);
        const hasMissingAttributes = dealerProduct?.rateRequirements?.hasMissingField;
        const hasRates = rates.find(
            (ratedProduct) => ratedProduct.productCode === product.productCode && ratedProduct.rates?.length
        );

        if (!hasMissingAttributes && (!dealerProduct || hasRates)) {
            product.selected = true;
        }
    });
    return products;
};

export const unselectAllEligibleProducts = ({ products, dealerProducts }: VehicleProtectionState): DraftDealProduct[] => {
    products
        .filter((product) => !product.isManualProduct)
        .forEach((product) => {
            const hasMissingAttributes = dealerProducts.find((dealerProduct) => dealerProduct.productCode === product.productCode)
                ?.rateRequirements?.hasMissingField;
            if (!hasMissingAttributes) {
                product.selected = false;
            }
        });
    return products;
};

/**
 * Compares Dealxg product with vpp rate and returns true if it's linked with each other by rate attributes
 * (coverage length, coverage miles, product deductible and product service interval).
 * In case rate doesn't have attributes - it matches the product as well
 */
export const doesRateMatchProduct = (
    rate: Rate,
    product: DraftDealProduct,
    rateAttributes: RatedProtectionProduct['rateAttributes']
) => {
    return rateAttributes.every((rateAttribute): boolean => {
        let isMatchingRate = false;

        switch (rateAttribute) {
            case 'productProviderPlan':
                isMatchingRate = rate.productProviderPlan === product.productProviderPlan;

                break;
            case 'productCoverageLength':
                isMatchingRate = rate.productCoverageLength === product.productCoverageLength;

                break;
            case 'productCoverageMiles':
                isMatchingRate = rate.productCoverageMiles === product.productCoverageMiles;

                break;
            case 'productDeductible':
                isMatchingRate =
                    rate.productDeductible === product.productDeductible &&
                    rate.deductibleApplyType === product.productDeductibleMethod;

                break;
            case 'productServiceInterval':
                isMatchingRate = rate.productServiceInterval === product.productServiceInterval;

                break;
            default:
                isMatchingRate = true;

                break;
        }

        return isMatchingRate;
    });
};

export const getSelectedRate = (
    { rates = [], rateAttributes = [] }: Partial<Pick<RatedProtectionProduct, 'rates' | 'rateAttributes'>> = {},
    product: DraftDealProduct
): Rate | undefined => {
    let matchingRate;

    rates.forEach((rate) => {
        if (doesRateMatchProduct(rate, product, rateAttributes)) {
            matchingRate = rate;
        }
    });

    return matchingRate;
};

const hasTermFieldValue = (value: string | number | null | undefined): boolean => value != null && value !== '' && value !== 0;

const isDeductibleSet = (product: DraftDealProduct, isRatedDeductible: boolean): boolean => {
    const hasValue = isRatedDeductible ? product.productDeductible != null : hasTermFieldValue(product.productDeductible);

    return hasValue || hasTermFieldValue(product.productDeductibleMethod);
};

/**
 * Deductible is a single logical value split across two DealXG fields (value + method).
 * It is overridden when a value/method is set but the rates do not offer that exact pair:
 *
 * - Non-rated product, or deductible is not a rated attribute: a set value is a manual override.
 * - Rated deductible: overridden when no rate matches the joined value+method pair,
 *   mirroring the deductible match used by {@link doesRateMatchProduct}.
 */
const isDeductibleOverridden = (
    product: DraftDealProduct,
    rateAttributes: RatedProtectionProduct['rateAttributes'],
    rates: Rate[]
): boolean => {
    const isRatedDeductible = rateAttributes.includes('productDeductible');

    if (!isDeductibleSet(product, isRatedDeductible)) {
        return isRatedDeductible;
    }

    if (rateAttributes.length === 0 || !isRatedDeductible) {
        return true;
    }

    return !rates.some(
        (rate) =>
            rate.productDeductible === product.productDeductible && rate.deductibleApplyType === product.productDeductibleMethod
    );
};

const isSingleValueTermFieldOverridden = (
    field: Exclude<VppTermField, 'productDeductible'>,
    product: DraftDealProduct,
    rateAttributes: RatedProtectionProduct['rateAttributes'],
    rates: Rate[]
): boolean => {
    if (!rateAttributes.includes(TERM_FIELD_TO_RATE_ATTRIBUTE[field])) {
        return hasTermFieldValue(product[field]);
    }

    return rates.length > 0 ? !rates.some((rate) => rate[field] === product[field]) : !hasTermFieldValue(product[field]);
};

/**
 * Determines which VPP term fields have been overridden, derived purely from the product's
 * state in the DealXG deal, its rate attributes, and its available rates (no extra fields
 * stored on the deal).
 *
 * - Non-rated product (no rate attributes): any term field with a value is an override.
 * - Rated product: a term field is an override when its rate attribute is absent from `rateAttributes`,
 *   or when its value is not offered by any available rate (a manually typed value the rate card does
 *   not carry), or when a rated value has been cleared.
 * - Deductible (value + method) is evaluated jointly: see {@link isDeductibleOverridden}.
 *
 * @returns A map containing only the overridden term fields, each set to `true`.
 */
export const getOverriddenTermFields = (
    product: DraftDealProduct,
    rateAttributes: RatedProtectionProduct['rateAttributes'] = [],
    rates: Rate[] = []
): OverriddenTermFields => {
    return (Object.keys(TERM_FIELD_TO_RATE_ATTRIBUTE) as VppTermField[]).reduce<OverriddenTermFields>((overrides, field) => {
        const isOverridden =
            field === 'productDeductible'
                ? isDeductibleOverridden(product, rateAttributes, rates)
                : isSingleValueTermFieldOverridden(field, product, rateAttributes, rates);

        if (isOverridden) {
            overrides[field] = true;
        }

        return overrides;
    }, {});
};

export const toDarwinBaselineFields = (product: DraftDealProduct): DarwinBaselineProduct => ({
    productProviderName: product.productProviderName,
    productProviderPlan: product.productProviderPlan,
    productCoverageLength: product.productCoverageLength,
    productCoverageMiles: product.productCoverageMiles,
    productDeductible: product.productDeductible,
    productServiceInterval: product.productServiceInterval,
    productAddOnDetails: product.productAddOnDetails?.productAddOnSummary && {
        productAddOnSummary: { ...product.productAddOnDetails.productAddOnSummary }
    },
    productPrice: product.productPrice,
    profitLocation: product.profitLocation,
    isProductPriceCapitalized: product.isProductPriceCapitalized,
    selected: product.selected
});

export const toDarwinBaseLineProducts = (products: DraftDealProduct[]) => {
    return products.reduce((products, p) => {
        return p.productCode && !p.isManualProduct
            ? {
                  ...products,
                  [p.productCode]: toDarwinBaselineFields(p)
              }
            : products;
    }, {}) as DarwinBaselineProducts;
};

/**
 * Represents "Darwin never set this product"
 */
export const EMPTY_DARWIN_BASELINE_PRODUCT: DarwinBaselineProduct = { selected: false };

export const isProductDivergedFromBaseline = (product: DarwinBaselineProduct, baseline: DarwinBaselineProduct = {}): boolean => {
    const baselineIsProductPriceCapitalized =
        baseline.isProductPriceCapitalized === undefined ? true : baseline.isProductPriceCapitalized;
    const productIsProductPriceCapitalized =
        product.isProductPriceCapitalized === undefined ? true : product.isProductPriceCapitalized;
    return (
        product.productProviderName !== baseline.productProviderName ||
        product.productProviderPlan !== baseline.productProviderPlan ||
        product.productCoverageLength !== baseline.productCoverageLength ||
        product.productCoverageMiles !== baseline.productCoverageMiles ||
        product.productDeductible !== baseline.productDeductible ||
        product.productServiceInterval !== baseline.productServiceInterval ||
        !_.isEqual(product.productAddOnDetails?.productAddOnSummary, baseline.productAddOnDetails?.productAddOnSummary) ||
        product.productPrice !== baseline.productPrice ||
        product.profitLocation !== baseline.profitLocation ||
        productIsProductPriceCapitalized !== baselineIsProductPriceCapitalized ||
        product.selected !== baseline.selected
    );
};

const DIVERGABLE_FIELDS: (keyof DarwinBaselineProduct)[] = [
    'productProviderName',
    'productProviderPlan',
    'productCoverageLength',
    'productCoverageMiles',
    'productDeductible',
    'productServiceInterval',
    'productPrice',
    'profitLocation',
    'selected'
];

const copyFieldIfDiverged = <K extends keyof DarwinBaselineProduct>(
    delta: DarwinBaselineProduct,
    product: DarwinBaselineProduct,
    baseline: DarwinBaselineProduct,
    field: K
): void => {
    if (product[field] !== baseline[field]) {
        delta[field] = baseline[field];
    }
};

const getDivergedBaselineFields = (product: DarwinBaselineProduct, baseline: DarwinBaselineProduct): DarwinBaselineProduct => {
    const baselineIsProductPriceCapitalized =
        baseline.isProductPriceCapitalized === undefined ? true : baseline.isProductPriceCapitalized;
    const productIsProductPriceCapitalized =
        product.isProductPriceCapitalized === undefined ? true : product.isProductPriceCapitalized;
    const delta: DarwinBaselineProduct = {};

    DIVERGABLE_FIELDS.forEach((field) => copyFieldIfDiverged(delta, product, baseline, field));

    if (!_.isEqual(product.productAddOnDetails?.productAddOnSummary, baseline.productAddOnDetails?.productAddOnSummary)) {
        delta.productAddOnDetails = baseline.productAddOnDetails;
    }
    if (productIsProductPriceCapitalized !== baselineIsProductPriceCapitalized) {
        delta.isProductPriceCapitalized = baselineIsProductPriceCapitalized;
    }

    return delta;
};

export const getDarwinBaselineOverrides = (
    updatedProducts: DraftDealProduct[],
    baselineProducts: DarwinBaselineProducts
): DarwinBaselineProducts => {
    const currentFieldsByCode = toDarwinBaseLineProducts(updatedProducts.filter((p) => p.selected));
    const allProductFieldsByCode = toDarwinBaseLineProducts(updatedProducts);
    const productCodes = new Set([...Object.keys(baselineProducts), ...Object.keys(currentFieldsByCode)]);

    return Array.from(productCodes).reduce<DarwinBaselineProducts>((overrides, productCode) => {
        const baseline = baselineProducts[productCode];
        const current = currentFieldsByCode[productCode] ?? allProductFieldsByCode[productCode] ?? EMPTY_DARWIN_BASELINE_PRODUCT;
        const isDiverged = isProductDivergedFromBaseline(current, baseline);

        if (isDiverged) {
            overrides[productCode] = baseline ? getDivergedBaselineFields(current, baseline) : EMPTY_DARWIN_BASELINE_PRODUCT;
        }

        return overrides;
    }, {});
};

const mapDynamicSurchargeToAddOn = (dynamicSurcharge: DynamicSurcharge): DealProductAddOn => {
    return {
        addOnCost: dynamicSurcharge.cost,
        addOnDescription: dynamicSurcharge.description,
        addOnPrice: dynamicSurcharge.price,
        addOnSurchargeCode: dynamicSurcharge.surchargeCode,
        isAddOnEditable: dynamicSurcharge.isEditable
    };
};

export const mapDynamicSurchargesToProductAddOnDetails = (
    dynamicSurcharges: DynamicSurcharge[] = []
): ProductAddOnDetailsNonOptional => {
    const selectedAddOns = dynamicSurcharges.filter((item) => item.value).map(mapDynamicSurchargeToAddOn);

    const unselectedAddOns = dynamicSurcharges.filter((item) => !item.value).map(mapDynamicSurchargeToAddOn);

    const addOnsCost = selectedAddOns
        .filter((addOn) => addOn.isAddOnEditable !== false)
        .reduce((acc: number, addOn: DealProductAddOn) => acc + addOn.addOnCost, 0);
    const addOnsPrice = selectedAddOns
        .filter((addOn) => addOn.isAddOnEditable !== false)
        .reduce((acc: number, addOn: DealProductAddOn) => acc + addOn.addOnPrice, 0);

    return {
        productAddOns: selectedAddOns,
        unselectedProductAddOns: unselectedAddOns,
        productAddOnSummary: {
            totalProductAddOnPrice: addOnsPrice,
            totalProductAddOnCost: addOnsCost
        }
    };
};

export const sortProducts = (products: DraftDealProduct[]): DraftDealProduct[] => {
    return _.clone(products).sort((a, b) => {
        const isSelectedA = a.selected ? 0 : 1;
        const isSelectedB = b.selected ? 0 : 1;
        const categoryCodeA = a.productCategoryCode ?? '';
        const categoryCodeB = b.productCategoryCode ?? '';

        return isSelectedA - isSelectedB || categoryCodeA.localeCompare(categoryCodeB);
    });
};

export const getPlansFromProductsRates = (rates: Rate[] | undefined = []): string[] => {
    return Array.from(
        new Set(
            rates.map((rate) => rate.productProviderPlan).filter((value: unknown): value is string => typeof value === 'string')
        )
    );
};

export const isVppProductSelected = (product: DraftDealProduct): boolean => {
    return product.selected === true;
};

export const isVppCapProduct = (product: DraftDealProduct): boolean => {
    return product.isProductPriceCapitalized ?? true;
};

export const isVppUpfrontProduct = (product: DraftDealProduct): boolean => {
    return product.isProductPriceCapitalized === false;
};

export const getUniqueProviderNames = (providerNames: (string | undefined)[]): string[] => {
    return providerNames.reduce<string[]>((unique, name) => {
        if (name && !unique.includes(name)) {
            unique.push(name);
        }
        return unique;
    }, []);
};
